var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { BoxLiftingForce } from "./overlay-elements/box-lifting-force";
import { BoxEditButtons } from "./overlay-elements/box-edit-buttons";
import { BoxParameters } from "./overlay-elements/box-parameters";
import { BoxSizeParameters } from "./overlay-elements/box-size-parameters";
import { NodeWarning } from "./overlay-elements/node-warning";
import { warningSize } from "./overlay-elements/node-warning.style";
import { OverlayData } from "store/types";
import { EditButtonIcon, } from "ui/components/elements/edit-buttons/edit-buttons";
import { useAppSelector, useAppDispatch, selectors, actions } from "store";
import { useWorldScreenPosition } from "hooks/playcanvas/world-screen-position";
import { objectPosition } from "3d/constants/common";
import { useNumberConversion } from "hooks/number-conversion";
import { Unit } from "utility/number";
/*
 * Every box overlay is anchored on the centre of the box's own top face. The
 * box model's panels hang below their root entity, which sits at
 * `objectPosition.y`, so that surface is exactly `objectPosition.y` — no box
 * height to add.
 *
 * Getting this wrong is not a small error: the 2D view looks down from ~2.4 m
 * away, so an anchor even one metre above the surface projects most of the way
 * again outwards from the centre of the view. Badges then drift off their own
 * box and, for boxes near the edge of a full pallet, clean off the canvas.
 */
var boxSurfaceWorldY = objectPosition.y;
// Offset (px) from the box centre up to the lifting-force badge, clearing the
// edit controls that sit centred on that point. Kept small enough that the
// badge's caret still lands on its own box: the pallet fills the 2D view, so
// even the smallest box a palletizing pattern produces is a good deal taller
// than this.
var liftingForceOffsetY = 30;
// Offset (px) from the box centre down to the parameter panel, clearing the
// edit controls that sit on the box itself.
var parametersOffsetY = 32;
// Half the height (px) of the edit-controls bar centred on the box: 28px
// buttons inside 5px of padding.
var editControlsHalfHeight = 20;
// Height (px) of the lifting-force badge including its caret gap, measured
// from its anchor upwards.
var liftingForceBadgeHeight = 27;
// Breathing room (px) between stacked overlays.
var stackGap = 4;
/**
 * Vertical offset (px) from the box centre to the *bottom* of the off-pallet
 * warning, so it never covers the other overlays on the same box.
 *
 * Alone, the warning is centred on the box like a node's warning. As soon as
 * the box has its edit controls (it is selected), those own the centre, so the
 * warning moves above them; and above the lifting-force badge as well when
 * there is one, because the badge's caret has to keep pointing at the box, not
 * at the warning.
 */
export var palletWarningOffsetY = function (_a) {
    var hasEditControls = _a.hasEditControls, hasLiftingForce = _a.hasLiftingForce;
    if (!hasEditControls)
        return warningSize / 2;
    if (hasLiftingForce) {
        return -(liftingForceOffsetY + liftingForceBadgeHeight + stackGap);
    }
    return -(editControlsHalfHeight + stackGap);
};
/** Where the centre of a box's top face is on screen. */
var useBoxScreenPosition = function (app, box, worldY) {
    return useWorldScreenPosition(app, box.position.x, worldY, box.position.z);
};
/**
 * Per-box 3D overlay badges. Renders a lifting-force label on each cardboard
 * box the cups are actually lifting; `selectBoxLiftStatuses` decides which
 * those are (multiple boxes, valid performance data, cups on the box), the
 * same gate the 3D box tint uses.
 *
 * When the latest calculation never reached Part Secured there is no verdict
 * to show, but the user still needs to see whether their changes head the
 * right way, so the badges then show provisional values from that result:
 * the peak force the box's cups produced and the vacuum level they reached
 * (`selectWithheldBoxLiftingForces`), in a dashed amber style.
 *
 * A palletizing pattern fills the pallet with boxes the tool never touches,
 * and those carry no force worth reading — badging all of them would bury the
 * pallet under a dozen overlapping labels.
 */
export var CardboardBoxOverlays = function (_a) {
    var app = _a.app;
    var t = useTranslation().t;
    var convertNumber = useNumberConversion();
    var cardboardBoxes = useAppSelector(selectors.selectCardboardBoxes);
    var activeLayer = useAppSelector(selectors.selectActiveLayer);
    var liftingForcePerBox = useAppSelector(selectors.selectLiftingForcePerBox);
    var boxLiftStatuses = useAppSelector(selectors.selectBoxLiftStatuses);
    var withheld = useAppSelector(selectors.selectWithheldBoxLiftingForces);
    var partSecuredLevel = useAppSelector(selectors.selectPartSecuredLevel);
    // The per-cup overlay shows ES forces while the ES overlay is on; the
    // badges follow it so the two never disagree.
    var overlayData = useAppSelector(selectors.selectOverlayData);
    var atEnergySave = overlayData === OverlayData.VACUUM_LEVEL_ES;
    // Only the active layer is on top and being picked from, so only badge
    // those boxes.
    var boxesInLayer = cardboardBoxes.filter(function (box) { return box.layer === activeLayer; });
    if (withheld) {
        var psLevel_1 = convertNumber(partSecuredLevel, Unit.KPA);
        return (_jsx(_Fragment, { children: withheld.boxes.map(function (peakForce) {
                var box = boxesInLayer.find(function (b) { return b.id === peakForce.boxId; });
                if (!box)
                    return null;
                var reached = convertNumber(peakForce.finalVacuumLevel, Unit.KPA);
                return (_jsx(BoxLiftingForceOverlay, { app: app, box: box, label: "#".concat(peakForce.boxNumber), vertical: peakForce.peakVertical, horizontal: peakForce.peakHorizontal, status: "provisional", note: t("canvas.box_peak_force"), detail: t("canvas.box_vacuum_of_part_secured", {
                        reached: reached.value,
                        partSecured: psLevel_1.value,
                        unit: psLevel_1.unit,
                    }) }, box.id));
            }) }));
    }
    return (_jsx(_Fragment, { children: boxesInLayer.map(function (box, index) {
            // The same gate as the 3D box tint: badge only the boxes shown as
            // lifted, so the label and the colour always agree.
            if (!boxLiftStatuses[box.id])
                return null;
            var boxForce = liftingForcePerBox.find(function (b) { return b.boxId === box.id; });
            if (!boxForce)
                return null;
            return (_jsx(BoxLiftingForceOverlay, { app: app, box: box, label: "#".concat(index + 1), vertical: atEnergySave ? boxForce.verticalAtES : boxForce.verticalAtPS, horizontal: atEnergySave ? boxForce.horizontalAtES : boxForce.horizontalAtPS, status: boxForce.partSecured ? "good" : "bad" }, box.id));
        }) }));
};
var BoxLiftingForceOverlay = function (_a) {
    var app = _a.app, box = _a.box, badge = __rest(_a, ["app", "box"]);
    var position = useBoxScreenPosition(app, box, boxSurfaceWorldY);
    if (!position)
        return null;
    return (_jsx(BoxLiftingForce, __assign({}, badge, { centerX: position.x, centerY: position.y - liftingForceOffsetY })));
};
/**
 * Edit controls over the selected cardboard box: duplicate, rotate and delete.
 * The rotate control is only offered when the box footprint is not square (a
 * square box looks identical under rotation), and delete is only offered when
 * more than one box exists (the last box can never be removed).
 *
 * While a palletizing pattern owns the row, only rotate is offered. Turning one
 * box is the same kind of edit as dragging one — which the scene has always
 * allowed — and both drop the row to "custom", so the rest of the controls
 * appear along with it. Duplicate and delete stay out of reach until then: the
 * box count is a *result* of the pattern (it is what the preset list shows next
 * to each layout), so changing it by hand while a pattern is named would
 * contradict the row's own description of itself.
 */
export var CardboardBoxEditOverlays = function (_a) {
    var app = _a.app, controlsRef = _a.controlsRef;
    var selectedBox = useSelectedBoxInLayer();
    var _b = useBoxEditCapabilities(selectedBox), canDuplicate = _b.canDuplicate, canRotate = _b.canRotate, canRemove = _b.canRemove, hasAny = _b.hasAny;
    // Nothing left to offer — a square box in a patterned row — so show no
    // controls rather than an empty button bar.
    if (!selectedBox || !hasAny)
        return null;
    return (_jsx(BoxEditOverlay, { app: app, box: selectedBox, canDuplicate: canDuplicate, canRotate: canRotate, canRemove: canRemove, containerRef: controlsRef }));
};
/** The selected box, if it is on the layer currently shown — the only one that
 * can be edited. */
var useSelectedBoxInLayer = function () {
    var cardboardBoxes = useAppSelector(selectors.selectCardboardBoxes);
    var selectedBoxId = useAppSelector(selectors.selectSelectedBoxId);
    var activeLayer = useAppSelector(selectors.selectActiveLayer);
    var selectedBox = cardboardBoxes.find(function (box) { return box.id === selectedBoxId; });
    return (selectedBox === null || selectedBox === void 0 ? void 0 : selectedBox.layer) === activeLayer ? selectedBox : undefined;
};
/**
 * Which edit controls a box gets (see `CardboardBoxEditOverlays`). Shared with
 * the off-pallet warning, which has to know whether the control bar is on the
 * box to stay clear of it.
 */
var useBoxEditCapabilities = function (box) {
    var _a;
    var cardboardBoxes = useAppSelector(selectors.selectCardboardBoxes);
    var objectSize = useAppSelector(selectors.selectObjectSize);
    var layerPatterns = useAppSelector(selectors.selectLayerPatterns);
    if (!box) {
        return {
            canDuplicate: false,
            canRotate: false,
            canRemove: false,
            hasAny: false,
        };
    }
    var hasPattern = Boolean(layerPatterns[box.layer]);
    // Delete is only offered when the box isn't the last one on its layer
    // (matches the per-layer remove guard in the scene reducer).
    var boxesInLayer = cardboardBoxes.filter(function (other) { return other.layer === box.layer; });
    var canDuplicate = !hasPattern;
    var size = (_a = box.size) !== null && _a !== void 0 ? _a : objectSize;
    var canRotate = size.x !== size.z;
    var canRemove = !hasPattern && boxesInLayer.length > 1;
    return {
        canDuplicate: canDuplicate,
        canRotate: canRotate,
        canRemove: canRemove,
        hasAny: canDuplicate || canRotate || canRemove,
    };
};
/**
 * The parameters for the box selected in the scene — the same panel the
 * designer's sketches put under a box on the pallet: this box's own size, then
 * the object's parameters every box shares.
 *
 * Shown for any selected box, pattern or not: unlike moving and turning boxes,
 * editing the object itself is always allowed (and a shared size change simply
 * lays the pattern out again). Giving this one box a size of its own makes the
 * layer hand-arranged, like dragging it does.
 */
export var CardboardBoxParameterOverlays = function (_a) {
    var app = _a.app, insideRefs = _a.insideRefs;
    var cardboardBoxes = useAppSelector(selectors.selectCardboardBoxes);
    var selectedBoxId = useAppSelector(selectors.selectSelectedBoxId);
    var activeLayer = useAppSelector(selectors.selectActiveLayer);
    var boxesInLayer = cardboardBoxes.filter(function (box) { return box.layer === activeLayer; });
    var selectedIndex = boxesInLayer.findIndex(function (box) { return box.id === selectedBoxId; });
    if (selectedIndex < 0)
        return null;
    return (_jsx(BoxParameterOverlay, { app: app, box: boxesInLayer[selectedIndex], 
        /* The same "#n" the lifting-force badges use, so the panel is visibly
           about the box the user clicked. */
        label: "#".concat(selectedIndex + 1), insideRefs: insideRefs }));
};
var BoxParameterOverlay = function (_a) {
    var app = _a.app, box = _a.box, label = _a.label, insideRefs = _a.insideRefs;
    var t = useTranslation().t;
    var dispatch = useAppDispatch();
    var position = useBoxScreenPosition(app, box, boxSurfaceWorldY);
    if (!position)
        return null;
    return (_jsx(BoxParameters, { centerX: position.x, centerY: position.y + parametersOffsetY, title: t("cardboard_box.title", { label: label }), insideRefs: insideRefs, 
        /* Clearing the box selection is what closes the panel; it deliberately
           leaves the sidebar and camera view alone. */
        onClose: function () { return dispatch(actions.deselectBox()); }, children: _jsx(BoxSizeParameters, { box: box }) }));
};
/**
 * Everything shown for the box selected in the scene: its edit controls and its
 * parameter panel.
 *
 * They are two separate overlays but one piece of UI, and they have to be
 * rendered together so the panel knows the controls are part of it. The panel
 * closes on a pointer outside itself, via a `document` capture-phase listener —
 * which runs *before* React hands a click to a button in a sibling overlay. So
 * without this shared ref, clicking a control would deselect the box and
 * unmount the control before its own `onClick` ever ran: the button appeared to
 * do nothing at all.
 */
export var CardboardBoxSelectionOverlays = function (_a) {
    var app = _a.app;
    var editControlsRef = useRef(null);
    return (_jsxs(_Fragment, { children: [_jsx(CardboardBoxEditOverlays, { app: app, controlsRef: editControlsRef }), _jsx(CardboardBoxParameterOverlays, { app: app, insideRefs: [editControlsRef] })] }));
};
/**
 * Warning icon over any box whose centre has been dragged off the pallet. Only
 * the active layer is on top and being worked on, so only its boxes are flagged.
 * Independent of performance data — it's a layout warning, shown as soon as a
 * box leaves the pallet footprint.
 *
 * Hovering the icon explains it; the same text is also shown as a notification
 * while the box is selected (see the notification panel), matching how a
 * node's warnings are surfaced.
 */
export var CardboardBoxPalletWarnings = function (_a) {
    var app = _a.app;
    var cardboardBoxes = useAppSelector(selectors.selectCardboardBoxes);
    var activeLayer = useAppSelector(selectors.selectActiveLayer);
    var boxesOutsidePallet = useAppSelector(selectors.selectBoxesOutsidePallet);
    var boxLiftStatuses = useAppSelector(selectors.selectBoxLiftStatuses);
    var selectedBox = useSelectedBoxInLayer();
    var selectedBoxHasEditControls = useBoxEditCapabilities(selectedBox).hasAny;
    var outsideIds = new Set(boxesOutsidePallet);
    var flaggedBoxes = cardboardBoxes.filter(function (box) { return box.layer === activeLayer && outsideIds.has(box.id); });
    return (_jsx(_Fragment, { children: flaggedBoxes.map(function (box) { return (_jsx(BoxPalletWarningOverlay, { app: app, box: box, offsetY: palletWarningOffsetY({
                hasEditControls: box.id === (selectedBox === null || selectedBox === void 0 ? void 0 : selectedBox.id) && selectedBoxHasEditControls,
                // The same gate `CardboardBoxOverlays` uses for the badge.
                hasLiftingForce: Boolean(boxLiftStatuses[box.id]),
            }) }, box.id)); }) }));
};
var BoxPalletWarningOverlay = function (_a) {
    var app = _a.app, box = _a.box, offsetY = _a.offsetY;
    var t = useTranslation().t;
    var position = useBoxScreenPosition(app, box, boxSurfaceWorldY);
    if (!position)
        return null;
    return (_jsx(NodeWarning, { nodeCenterX: position.x, nodeCenterY: position.y + offsetY, anchor: "above", tooltip: t("cardboard_box.outside_pallet") }));
};
var BoxEditOverlay = function (_a) {
    var app = _a.app, box = _a.box, canDuplicate = _a.canDuplicate, canRotate = _a.canRotate, canRemove = _a.canRemove, containerRef = _a.containerRef;
    var dispatch = useAppDispatch();
    var position = useBoxScreenPosition(app, box, boxSurfaceWorldY);
    if (!position)
        return null;
    var editActions = [
        canDuplicate
            ? {
                icon: EditButtonIcon.DUPLICATE,
                action: function (event) {
                    event.stopPropagation();
                    dispatch(actions.duplicateCardboardBox(box.id));
                },
            }
            : undefined,
        canRotate
            ? {
                icon: EditButtonIcon.ROTATE_CLOCKWISE,
                action: function (event) {
                    event.stopPropagation();
                    dispatch(actions.rotateCardboardBox(box.id));
                },
            }
            : undefined,
        canRemove
            ? {
                icon: EditButtonIcon.DELETE,
                action: function (event) {
                    event.stopPropagation();
                    dispatch(actions.removeCardboardBox(box.id));
                },
                isDelete: true,
            }
            : undefined,
    ].filter(Boolean);
    return (_jsx(BoxEditButtons, { centerX: position.x, centerY: position.y, actions: editActions, containerRef: containerRef }));
};
